/**
 * Geometrie-Bausteine in Metern. UVs sind metrisch (1 = 1 m), damit Texturen über `tileCm`
 * maßstabsgetreu kacheln – egal ob Leiste 30 cm oder 150 cm lang ist.
 */
import * as THREE from 'three'
import type { FrameProfileShape, ResolvedFrame } from './types.ts'

export const MM = 0.001

/** Quader mit metrischen UVs; Materialgruppen wie BoxGeometry (px, nx, py, ny, pz, nz) */
export function metricBox(w: number, h: number, d: number): THREE.BoxGeometry {
  const g = new THREE.BoxGeometry(w, h, d)
  const uv = g.getAttribute('uv') as THREE.BufferAttribute
  // je Seite 4 Vertices; Seitenmaße (u, v) in Metern
  const dims: Array<[number, number]> = [[d, h], [d, h], [w, d], [w, d], [w, h], [w, h]]
  for (let face = 0; face < 6; face++) {
    const [fu, fv] = dims[face]!
    for (let i = 0; i < 4; i++) {
      const idx = face * 4 + i
      uv.setXY(idx, uv.getX(idx) * fu, uv.getY(idx) * fv)
    }
  }
  uv.needsUpdate = true
  return g
}

/** Profilpunkt: x = Abstand nach außen von der Innenkante (mm), z = Tiefe (mm, + = zum Betrachter) */
export type ProfilePoint = [number, number]

/**
 * Rahmen aus 4 Leisten mit 45°-Gehrung. `profile` ist ein geschlossenes Polygon (gegen den Uhrzeigersinn
 * egal – Material ist doppelseitig). Innenöffnung W×H in Metern, Mittelpunkt im Ursprung.
 * `shade` je Segment (0..1) dunkelt z. B. Innenflächen einer Schattenfuge ab (Vertexfarben).
 */
export function mitredFrameGeometry(W: number, H: number, profile: ProfilePoint[], shade?: number[]): THREE.BufferGeometry {
  const pos: number[] = []
  const uvs: number[] = []
  const cols: number[] = []
  const pts = profile.map(([x, z]) => [x * MM, z * MM] as const)
  // Profil-Umfang für V-Koordinate
  const vAt: number[] = [0]
  for (let i = 1; i <= pts.length; i++) {
    const a = pts[i - 1]!, b = pts[i % pts.length]!
    vAt.push(vAt[i - 1]! + Math.hypot(b[0] - a[0], b[1] - a[1]))
  }

  type Side = { half: number; place: (a: number, off: number, z: number) => [number, number, number] }
  const sides: Side[] = [
    { half: W / 2, place: (a, off, z) => [a, H / 2 + off, z] }, // oben
    { half: W / 2, place: (a, off, z) => [-a, -H / 2 - off, z] }, // unten
    { half: H / 2, place: (a, off, z) => [-W / 2 - off, a, z] }, // links
    { half: H / 2, place: (a, off, z) => [W / 2 + off, -a, z] }, // rechts
  ]

  for (const s of sides) {
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i]!, q = pts[(i + 1) % pts.length]!
      const lp = s.half + p[0], lq = s.half + q[0]
      const v0 = vAt[i]!, v1 = vAt[i + 1]!
      const c = shade?.[i] ?? 1
      const A = s.place(-lp, p[0], p[1]), B = s.place(lp, p[0], p[1])
      const C = s.place(lq, q[0], q[1]), D = s.place(-lq, q[0], q[1])
      // zwei Dreiecke A B C, A C D
      pos.push(...A, ...B, ...C, ...A, ...C, ...D)
      uvs.push(-lp, v0, lp, v0, lq, v1, -lp, v0, lq, v1, -lq, v1)
      for (let k = 0; k < 6; k++) cols.push(c, c, c)
    }
  }
  const g = new THREE.BufferGeometry()
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3))
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))
  g.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3))
  g.computeVertexNormals()
  return g
}

/**
 * Profilquerschnitt je Rahmentyp. Bezug: z = 0 ist die Bildoberfläche, x = 0 die Bildkante.
 * bodyDepthMm = Tiefe des Bildkörpers (Leinwand inkl. Keilrahmen, Platte …).
 */
export function frameProfile(frame: ResolvedFrame, bodyDepthMm: number): { points: ProfilePoint[]; shade: number[] } {
  const f = Math.max(3, frame.faceWidthMm)
  if (frame.type === 'shadow-gap') {
    // L-Profil: Wand um das Bild mit Fuge g, Boden läuft unter dem Bild durch
    const g = Math.max(0, frame.gapMm)
    const depth = Math.max(frame.depthMm, bodyDepthMm + 5)
    const front = 0 // Vorderkante bündig mit der Bildfläche
    const back = front - depth
    const floor = -bodyDepthMm
    const pts: ProfilePoint[] = [
      [g, front], [g + f, front], [g + f, back], [-15, back], [-15, floor], [g, floor],
    ]
    // Segmente: Stirn, Außen, Rücken, Bodenkante, Boden(innen, sichtbar in Fuge), Innenwand
    return { points: pts, shade: [1, 0.92, 0.6, 0.5, 0.28, 0.32] }
  }
  // klassischer Rahmen: Falz überdeckt das Bild um r, steht h vor dem Bild
  const r = Math.max(0, frame.rebateMm)
  const d = Math.max(8, frame.depthMm)
  const h = Math.min(14, d * 0.45)
  const o = f - r // Außenkante
  const back = h - d
  const top = profileTop(frame.shape, -r, o, h)
  const pts: ProfilePoint[] = [[-r, 0], ...top, [o, back], [0, back], [0, 0]]
  const shade = pts.map((_, i) => (i === pts.length - 1 || i === pts.length - 2 ? 0.45 : i === 0 ? 0.7 : 1))
  return { points: pts, shade }
}

function profileTop(shape: FrameProfileShape, x0: number, x1: number, h: number): ProfilePoint[] {
  const w = x1 - x0
  switch (shape) {
    case 'bevel':
      return [[x0, h * 0.35], [x0 + w * 0.35, h], [x1, h]]
    case 'stepped':
      return [[x0, h * 0.55], [x0 + w * 0.35, h * 0.55], [x0 + w * 0.35, h], [x1, h]]
    case 'rounded': {
      const out: ProfilePoint[] = [[x0, h * 0.7]]
      const R = Math.min(w * 0.45, h * 0.9)
      out.push([x1 - R, h])
      for (let i = 1; i <= 6; i++) {
        const a = (i / 6) * (Math.PI / 2)
        out.push([x1 - R + Math.sin(a) * R, h - R + Math.cos(a) * R])
      }
      return out
    }
    default:
      return [[x0, h], [x1, h]]
  }
}

/** Platte mit Loch (Passepartout): Außen W×H, Innen w×h, Dicke d, Vorderseite bei z=0 */
export function matBoardGeometry(W: number, H: number, w: number, h: number, d: number): THREE.ExtrudeGeometry {
  const s = new THREE.Shape()
  s.moveTo(-W / 2, -H / 2); s.lineTo(W / 2, -H / 2); s.lineTo(W / 2, H / 2); s.lineTo(-W / 2, H / 2); s.closePath()
  const hole = new THREE.Path()
  hole.moveTo(-w / 2, -h / 2); hole.lineTo(-w / 2, h / 2); hole.lineTo(w / 2, h / 2); hole.lineTo(w / 2, -h / 2); hole.closePath()
  s.holes.push(hole)
  const g = new THREE.ExtrudeGeometry(s, { depth: d, bevelEnabled: true, bevelThickness: d * 0.4, bevelSize: d * 0.6, bevelSegments: 1 })
  g.translate(0, 0, -d)
  return g
}
